import type { Dialog as BaseDialog } from '@base-ui/react'
import { type FC, useState } from 'react'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { ListingActivityEntry } from '@/features/luxe-submissions/components/listing-activity-entry'
import { useGetFinalReviewListingActivity } from '@/features/luxe-submissions/queries/use-get-final-review-listing-activity'
import { useTranslation } from '@/translations/i18n'

interface FinalReviewListingActivityDialogProps {
	handle: BaseDialog.Handle<{ listingId: string }>
}

export const FinalReviewListingActivityDialog: FC<
	FinalReviewListingActivityDialogProps
> = ({ handle }) => {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)

	return (
		<Dialog handle={handle} onOpenChange={setOpen} open={open}>
			{({ payload }) => {
				const data = payload as { listingId: string } | undefined
				return (
					data !== undefined && (
						<DialogContent
							className='grid max-w-120 grid-rows-[auto_1fr_auto] overflow-hidden'
							showCloseButton={false}
						>
							<DialogHeader>
								{t('luxe_submissions:listing_activity')}
							</DialogHeader>

							<ActivityContent listingId={data.listingId} />

							<DialogFooter className='flex flex-row justify-end'>
								<DialogClose
									render={
										<ButtonLegacy
											type='button'
											variant='brand'
										/>
									}
								>
									{t('common:done')}
								</DialogClose>
							</DialogFooter>
						</DialogContent>
					)
				)
			}}
		</Dialog>
	)
}

function ActivityContent({ listingId }: { listingId: string }) {
	const { t } = useTranslation()
	const { data, isLoading } = useGetFinalReviewListingActivity(listingId)

	return (
		<DialogBody>
			<DialogTitle>{t('luxe_submissions:activity_log')}</DialogTitle>

			{isLoading ? (
				<div className='flex flex-col'>
					{['skeleton-1', 'skeleton-2', 'skeleton-3'].map((id, i) => (
						<div
							className={`flex flex-col gap-2 py-4 ${i < 2 ? 'border-neutral-200 border-b' : ''}`}
							key={id}
						>
							<Skeleton height={14} width={120} />
							<div className='flex items-center gap-2'>
								<Skeleton height={14} width={80} />
								<Skeleton height={14} width={100} />
							</div>
							<div className='flex items-center gap-2'>
								<Skeleton height={22} width={70} />
								<Skeleton height={22} width={70} />
							</div>
							<Skeleton height={12} width={90} />
						</div>
					))}
				</div>
			) : (
				<div className='flex flex-col gap-4'>
					{data?.map((entry) => (
						<ListingActivityEntry entry={entry} key={entry.id} />
					))}
				</div>
			)}
		</DialogBody>
	)
}
